import { useCountries } from "@/pages/forms/countries";
/**
 * BusinessSignup
 * ──────────────
 * Single unified signup form for every HustlePlate module.
 * Driven entirely by manifests in `businessSignupManifests.js`.
 *
 *   <BusinessSignup
 *     manifestId="plaza_vendor"
 *     onSubmit={(payload) => …}     // optional — called after backend POST
 *     onError={(err) => …}          // optional
 *     prefill={{                    // optional — pre-fill from directory
 *       directory_id: "babylon-brisbane-brisbane-city-2",
 *       country:      "au",
 *     }}
 *   />
 *
 * The form:
 *   1. Renders sections in order
 *   2. Validates everything before submit
 *   3. Wires <AbnLookup> if `abn` field is in the manifest
 *   4. POSTs to /api/business-signup/{manifestId}
 *   5. Server upserts directory record + registers /api/abn/link
 *   6. Calls onSubmit on success
 */
import { useEffect, useState, useMemo } from "react";
import { Loader2, CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";
import { FIELDS } from "@/lib/businessSignupFields";
import { getManifest } from "@/lib/businessSignupManifests";
import AbnLookup from "@/components/AbnLookup";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export default function BusinessSignup({
  manifestId,
  prefill = null,
  onSubmit,
  onError,
}) {
  const manifest = getManifest(manifestId);
  const [form, setForm] = useState(() => initialForm(manifest, prefill));
  const [errors, setErrors] = useState({});
  const [abnInfo, setAbnInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Pre-fill from directory if requested.
  useEffect(() => {
    if (!prefill?.directory_id) return;
    const country = prefill.country || manifest.country || "au";
    fetch(`${API}/api/abn/by-directory/${prefill.directory_id}?country=${country}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setForm((f) => ({
          ...f,
          business_name: d.name || f.business_name,
          suburb:        d.suburb || f.suburb,
          postcode:      d.postcode || f.postcode,
        }));
        if (d.abn) setAbnInfo({
          abn: d.abn,
          entity_name: d.registry?.entity_name,
          business_name: d.name,
          status: d.abn_status === "verified" ? "verified" : d.abn_status,
          trading_names: d.registry?.trading_names || [],
          modules: d.registry?.modules || {},
        });
      })
      .catch(() => {});
  }, [prefill?.directory_id, prefill?.country, manifest.country]);

  if (!manifest) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-900">
        Unknown manifest: <code>{manifestId}</code>
      </div>
    );
  }

  const onField = (fid, val) => {
    setForm((f) => ({ ...f, [fid]: val }));
    setErrors((e) => ({ ...e, [fid]: null }));
  };

  // Multi-field setter — used by the smart address picker so picking an
  // address fills street, suburb, state and postcode in one shot.
  const onAddressPick = (val) => {
    setForm((f) => ({
      ...f,
      address_line_1: val?.label || val?.line1 || "",
      suburb: val?.suburb || f.suburb || "",
      state: val?.state || f.state || "",
      postcode: val?.postcode || f.postcode || "",
      _address_resolved: val ? {
        place_id: val.place_id, lat: val.lat, lng: val.lng,
        manual: !!val.manual,
      } : null,
    }));
    setErrors((e) => ({
      ...e,
      address_line_1: null, suburb: null, state: null, postcode: null,
    }));
  };

  const validate = () => {
    const next = {};
    for (const section of manifest.sections) {
      for (const fid of section.fields) {
        const def = FIELDS[fid];
        if (!def) continue;
        // ABN handled separately via <AbnLookup>.
        if (def.type === "business_id") continue;
        const err = (def.validate || (() => null))(form[fid]);
        if (err) next[fid] = err;
      }
    }
    // ABN requirement (unless explicitly opted out via AbnLookup -> status=not_required)
    if (manifest.business_id_required) {
      if (!abnInfo || (abnInfo.status !== "verified" && abnInfo.status !== "not_required")) {
        next.abn = "Verify your ABN to continue";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        manifest_id: manifest.id,
        module:      manifest.module,
        country:     manifest.international ? form.country : manifest.country,
        form,
        abn:         abnInfo?.status === "verified" ? abnInfo : null,
        abn_status:  abnInfo?.status === "verified" ? "verified" :
                     abnInfo?.status === "not_required" ? "not_required" :
                     manifest.business_id_required ? "pending" : "not_required",
      };
      const r = await fetch(`${API}/api/business-signup/${manifest.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Signup failed");
      setSuccess(j);
      toast.success("You're in. We've created your directory listing.");
      onSubmit?.(j);
    } catch (e) {
      toast.error(e.message || "Signup failed");
      onError?.(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center space-y-3" data-testid="bs-success">
        <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
        <h2 className="text-xl font-bold text-emerald-900">You&apos;re in.</h2>
        <p className="text-[13px] text-emerald-800">
          We&apos;ve registered <strong>{success.business?.business_name}</strong> on{" "}
          {success.module} and linked it to the {success.country?.toUpperCase()} directory.
        </p>
        {success.directory_id && (
          <p className="text-[11px] font-mono text-emerald-700">directory id: {success.directory_id}</p>
        )}
      </div>
    );
  }

  return (
    <div data-testid={`business-signup-${manifest.id}`} className="space-y-6 max-w-2xl">
      <header>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
          {manifest.module}
          {manifest.country ? ` · ${manifest.country.toUpperCase()}` : ""}
          {manifest.international ? " · international" : ""}
        </p>
        <h1 className="text-2xl font-bold text-stone-900">{manifest.title}</h1>
        {manifest.subtitle && (
          <p className="text-[13px] text-stone-600 mt-1">{manifest.subtitle}</p>
        )}
      </header>

      {manifest.sections.map((sec) => {
        // When a smart address field is in the section, we render it as
        // a single picker and skip the discrete suburb/state/postcode
        // siblings (they're filled in by the picker).
        const hasSmartAddress = sec.fields.some(
          (fid) => FIELDS[fid]?.type === "address_au"
        );
        const skipFids = hasSmartAddress
          ? new Set(["suburb", "state", "postcode"]) : new Set();
        return (
        <section
          key={sec.id}
          data-testid={`bs-section-${sec.id}`}
          className="bg-white border border-stone-200 rounded-xl p-5 space-y-3"
        >
          <h2 className="text-[11px] uppercase tracking-wider font-bold text-stone-600 border-b border-stone-100 pb-2">
            {sec.title}
          </h2>
          <div className="space-y-4">
            {sec.fields.map((fid) => {
              const def = FIELDS[fid];
              if (!def) return null;
              if (skipFids.has(fid)) return null;
              if (def.type === "business_id") {
                return (
                  <AbnLookup
                    key={fid}
                    value={form[fid] || ""}
                    onChange={(v) => onField(fid, v)}
                    onResolve={setAbnInfo}
                    required={manifest.business_id_required}
                    testId={`bs-${fid}`}
                    label={def.label}
                  />
                );
              }
              if (def.type === "address_au") {
                return (
                  <div key={fid} className="space-y-1"
                       data-testid={`bs-field-${fid}`}>
                    <label className="block text-[11px] uppercase tracking-wider font-bold text-stone-600">
                      {def.label}
                    </label>
                    <div className="bs-address-host">
                      <AddressAutocomplete
                        value={form._address_resolved ? {
                          place_id: form._address_resolved.place_id,
                          label: form.address_line_1 || "",
                          lat: form._address_resolved.lat,
                          lng: form._address_resolved.lng,
                          state: form.state, postcode: form.postcode,
                          suburb: form.suburb,
                          manual: form._address_resolved.manual,
                        } : null}
                        onChange={onAddressPick}
                        testIdPrefix={`bs-field-${fid}`}
                        placeholder={def.placeholder || "Start typing your street address…"}
                      />
                    </div>
                    {errors[fid] && (
                      <p className="text-[11px] text-red-700">
                        <AlertCircle className="inline w-3 h-3 mr-1" />
                        {errors[fid]}
                      </p>
                    )}
                  </div>
                );
              }
              return (
                <FieldRow
                  key={fid}
                  id={fid}
                  def={def}
                  value={form[fid]}
                  err={errors[fid]}
                  onChange={(v) => onField(fid, v)}
                />
              );
            })}
          </div>
        </section>
      );
      })}

      <div className="flex items-center justify-between">
        <p className="text-[11px] text-stone-500">
          Everything will be stored in the {manifest.country?.toUpperCase() || form.country?.toUpperCase() || "selected"} directory.
        </p>
        <button
          onClick={submit}
          disabled={submitting}
          data-testid="bs-submit"
          className="px-5 py-2.5 rounded-xl bg-stone-900 text-white text-[14px] font-bold hover:bg-stone-700 disabled:opacity-40 flex items-center gap-2"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Create my listing
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Field renderer
// ──────────────────────────────────────────────────────────────────────
function FieldRow({ id, def, value, err, onChange }) {
  const countries=useCountries();
  const options=id==="country"?(def.options || []).map(option=>{
    const iso=option.value==="uk"?"GB":String(option.value).toUpperCase();
    const country=countries.find(row=>row.code===iso);
    return country?{...option,label:country.flag+" "+country.name}:option;
  }):def.options;
  const testId = `bs-field-${id}`;
  if (def.type === "checkbox") {
    return (
      <label className="flex items-start gap-2 text-[13px] text-stone-700 cursor-pointer">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testId}
          className="mt-0.5 h-4 w-4 rounded border-stone-300 text-stone-900"
        />
        <span className="flex-1">
          {def.label}
          {err && <span className="block text-[11px] text-red-700 mt-0.5"><AlertCircle className="inline w-3 h-3 mr-1" />{err}</span>}
        </span>
      </label>
    );
  }
  if (def.type === "select") {
    return (
      <div className="space-y-1">
        <label className="block text-[11px] uppercase tracking-wider font-bold text-stone-600">{def.label}</label>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className={`w-full px-3 py-2 rounded-md border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/30 ${err ? "border-red-400" : "border-stone-300"}`}
        >
          <option value="">— Select —</option>
          {(options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {err && <p className="text-[11px] text-red-700"><AlertCircle className="inline w-3 h-3 mr-1" />{err}</p>}
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <label className="block text-[11px] uppercase tracking-wider font-bold text-stone-600">{def.label}</label>
      <input
        type={def.type === "number" ? "number" : def.type === "tel" ? "tel" : def.type === "email" ? "email" : def.type === "url" ? "url" : "text"}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={def.placeholder || ""}
        maxLength={def.maxLength}
        data-testid={testId}
        className={`w-full px-3 py-2 rounded-md border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/30 ${err ? "border-red-400" : "border-stone-300"}`}
      />
      {err && <p className="text-[11px] text-red-700"><AlertCircle className="inline w-3 h-3 mr-1" />{err}</p>}
    </div>
  );
}

function initialForm(manifest, prefill) {
  const f = { terms: false, marketing_optin: false, co_op_membership: false };
  if (manifest?.international) f.country = "";
  if (prefill?.business_name) f.business_name = prefill.business_name;
  return f;
}
